// client/src/components/AccountDropdown.js
import React, { useState, useRef, useEffect } from 'react';
import { User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AboutModal from './AboutModal';
import FeedbackForm from './FeedbackForm';
import LoginButton from './LoginButton';

function AccountDropdown({ user, onProfileClick, setUser, onImportMap }) {
  const [open, setOpen] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const isAdmin = user && (user.role === 'admin' || user.role === 'founder');

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function getVoxemeraOrigin() {
    try {
      const host = String(window.location.hostname || '').toLowerCase();
      const isBeta = host.startsWith('beta.') || host.includes('beta');
      return isBeta ? 'https://beta.voxemera.com' : 'https://voxemera.com';
    } catch {
      return 'https://beta.voxemera.com';
    }
  }

  async function handleLogout(e) {
    e.preventDefault();
    setOpen(false);

    try {
      if (typeof setUser === 'function') setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('token');
    } catch {}

    const logoutUrl = '/auth/logout';

    try {
      const res = await fetch(logoutUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        await fetch(logoutUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        }).catch(() => {});
      }
    } catch {}

    try {
      window.location.assign('/');
    } catch {
      navigate('/');
    }
  }

  const handleMenuClick = (e, action) => {
    e.preventDefault();
    setOpen(false);

    if (action === 'Login') {
      return;
    } else if (action === 'My Profile') {
      if (onProfileClick) onProfileClick();
    } else if (action === 'My Maps') {
      navigate('/my-maps');
    } else if (action === 'Comms') {
      window.location.href = `${getVoxemeraOrigin()}/`;
    } else if (action === 'Import Map Data (JSON)') {
      if (user) {
        if (onImportMap) onImportMap();
      } else {
        alert('You need to be logged in to import map data.');
      }
    } else if (action === 'My Comments') {
      navigate('/my-comments');
    } else if (action === 'Favourites') {
      navigate('/favourites');
    } else if (action === 'Help / Documentation') {
      alert('"Help / Documentation" not implemented yet!');
    } else if (action === 'Feedback / Contact') {
      setShowFeedback(true);
    } else if (action === 'About') {
      setShowAbout(true);
    } else if (action === 'Terms & Privacy') {
      navigate('/privacy');
    } else if (action === 'Admin Dashboard') {
      alert('"Admin Dashboard" not implemented yet!');
    } else {
      alert(`"${action}" not implemented yet!`);
    }
  };

  return (
    <div className="account-dropdown" ref={dropdownRef}>
      <button
        className="account-dropdown-btn"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          fontWeight: user ? 500 : 400,
          fontSize: 16,
          background: 'none',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
        }}
        title={user ? user.username : 'Account'}
      >
        <User style={{ width: 24, height: 24 }} />
        {user && (
          <span style={{ marginLeft: 4, fontWeight: 600 }}>{user.username}</span>
        )}
        <span style={{ marginLeft: 4, fontSize: '1.1em' }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div className="account-dropdown-menu">
          {user ? (
            <div className="account-dropdown-user">
              <div><strong>{user.username}</strong></div>
              <div style={{ fontSize: '0.88em', color: '#aaa' }}>{user.email}</div>
            </div>
          ) : (
            <div style={{ padding: '12px 0', textAlign: 'center', borderBottom: '1px solid #333' }}>
              <LoginButton onLogin={setUser} />
            </div>
          )}

          {user && (
            <a href="#" className="account-dropdown-item" onClick={e => handleMenuClick(e, 'My Profile')}>
              My Profile
            </a>
          )}

          <a href="#" className="account-dropdown-item" onClick={e => handleMenuClick(e, 'My Maps')}>
            My Maps
          </a>

          <a
            href="#"
            className="account-dropdown-item"
            onClick={e => handleMenuClick(e, 'Comms')}
          >
            Comms
          </a>

          <a
            href="#"
            className="account-dropdown-item"
            onClick={e => handleMenuClick(e, 'Import Map Data (JSON)')}
            style={{ color: '#6fc3ff', fontWeight: 700 }}
          >
            Import Map Data (JSON)
          </a>

          {user && (
            <>
              <a href="#" className="account-dropdown-item" onClick={e => handleMenuClick(e, 'My Comments')}>
                My Comments
              </a>
              <a href="#" className="account-dropdown-item" onClick={e => handleMenuClick(e, 'Favourites')}>
                Favourites
              </a>
            </>
          )}

          <hr />

          <a
            href="https://point-atlas.io/emitter-tool"
            className="account-dropdown-item"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#9df2ff', fontWeight: 600 }}
          >
            Emitter Tool
          </a>

          <a href="#" className="account-dropdown-item" onClick={e => handleMenuClick(e, 'Help / Documentation')}>
            Help / Documentation
          </a>

          <a href="#" className="account-dropdown-item" onClick={e => handleMenuClick(e, 'Feedback / Contact')}>
            Feedback / Contact
          </a>

          <a
            href="#"
            className="account-dropdown-item"
            onClick={e => handleMenuClick(e, 'About')}
            style={{ borderTop: '1px solid #333', marginTop: 8 }}
          >
            About / Why We Exist
          </a>

          <a
            href="#"
            className="account-dropdown-item"
            onClick={e => handleMenuClick(e, 'Terms & Privacy')}
            style={{ color: '#9aaaff', fontWeight: 600 }}
          >
            Terms & Privacy
          </a>

          {isAdmin && (
            <>
              <hr />
              <a
                href="#"
                className="account-dropdown-item"
                style={{ color: '#ffc300' }}
                onClick={e => handleMenuClick(e, 'Admin Dashboard')}
              >
                Admin Dashboard
              </a>
            </>
          )}

          {user && (
            <>
              <hr />
              <a
                href="#"
                className="account-dropdown-item"
                style={{ color: '#ff5959', fontWeight: 600 }}
                onClick={handleLogout}
              >
                Logout
              </a>
            </>
          )}
        </div>
      )}

      <AboutModal open={showAbout} onClose={() => setShowAbout(false)} />
      {showFeedback && (
        <FeedbackForm user={user} onClose={() => setShowFeedback(false)} />
      )}
    </div>
  );
}

export default AccountDropdown;